import type { RootState } from '@core/redux/interface';
import type { MapboxStyle, Vector } from '@core/types';
import { projectSelectors } from '@projects/redux/slice';
import type { Project } from '@projects/types';
import { createSelector } from '@reduxjs/toolkit';
import { scanSelectors } from '@scans/redux/slice';
import type { Scan } from '@scans/types';
import type { BoundingBoxWithHeight, ScanSelection, VisibleScans } from '@viewer3D/types';
import type { Selector } from 'react-redux';
import { Euler, Vector3 } from 'three';
import { pointSelectors } from '@trenches/redux';
import { processingResultSelectors } from '@processingResults/redux';
import type { ProcessingResult } from '@processingResults/types';
import { vecFromLatLong } from '@core/logic';
import { elementSelectors } from '@elements/redux';
import { pointElementSelectors } from '@elements/redux/point-element.slice';
import { PointElement, SplineElement } from '@elements/types';
import type { Viewer3dState, VisibleElementUsage } from './interface';

const viewer3dSelector: Selector<RootState, Viewer3dState> = (state) => state.viewer3d;

const getViewer3dProp = <T>(prop: keyof Viewer3dState) =>
  createSelector([viewer3dSelector], (viewer3d) => viewer3d[prop] as unknown as T);

const selectProjectId = getViewer3dProp<string | undefined>('selectedProjectId');
const selectedProject = (state: RootState): Project | undefined => {
  const id = selectProjectId(state);
  return projectSelectors.selectById(id)(state);
};
const selectedScanId = getViewer3dProp<string | undefined>('selectedScanId');
const selectedTrenchId = getViewer3dProp<string | undefined>('selectedTrenchId');
const selectedElementIds = getViewer3dProp<string[]>('selectedElementIds');

const selectCameraViewport = getViewer3dProp<BoundingBoxWithHeight | undefined>('cameraViewport');
const selectVisibleElementUsage = getViewer3dProp<VisibleElementUsage>('visibleElementUsage');
const selectedPointId = getViewer3dProp<string | undefined>('selectedPointId');

const selectedScan = (state: RootState): Scan | undefined => {
  const id = selectedScanId(state);
  return scanSelectors.selectById(id)(state);
};

const firstSelectedElement = (state: RootState) => {
  const id = selectedElementIds(state)[0];
  if (!id) return undefined;
  return elementSelectors.selectById(id)(state) ?? pointElementSelectors.selectById(id)(state);
};

export const getScanPoint = (
  scan: Scan | undefined,
  processingResult: ProcessingResult | undefined,
): Vector | undefined => {
  if (!scan) return undefined;

  // 1. High-Precision Path: Use exact georeferenced 3D coordinates if loaded and valid
  const firstMesh = processingResult?.texturedMeshes?.[0];
  if (processingResult && firstMesh?.mesh?.offset) {
    const disableUserTransformation = scan.disableUserTransformation ?? false;
    const disableAutoAlignedPosition = scan.disableAutoAlignedPosition ?? true;

    let translation;
    let rotation;
    if (disableUserTransformation && !disableAutoAlignedPosition) {
      translation = processingResult.autoAlignmentTranslation;
      rotation = processingResult.autoAlignmentRotation;
    } else if (!disableUserTransformation && disableAutoAlignedPosition) {
      translation = processingResult.userTranslation;
      rotation = processingResult.userRotation;
    }

    const offset = firstMesh.mesh.offset;
    const x = translation?.x ?? offset.x;
    const y = translation?.y ?? offset.y;
    const z = translation?.z ?? offset.z;

    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
      return undefined;
    }

    const basePos = new Vector3(x, y, z);
    const boundingBox = firstMesh.texture?.boundingBox;

    if (boundingBox) {
      const localX = (boundingBox.minX + boundingBox.maxX) / 2;
      const localY = (boundingBox.minY + boundingBox.maxY) / 2;
      const localCenter = new Vector3(localX, localY, 0);

      // Calculate local center and rotate it to match scan orientation
      if (rotation) {
        const euler = new Euler(rotation.x, rotation.y, rotation.z);
        localCenter.applyEuler(euler);
      }

      return basePos.add(localCenter);
    }

    return basePos;
  }

  // 2. Safe Fallback Path: If processing result is loading/missing/failed,
  // gracefully fall back to coarse phone GPS so the camera still flies to the general area
  if (scan.location?.latitudeMean && scan.location?.longitudeMean) {
    const z = processingResult?.userTranslation?.z ?? 100.0;
    if (!Number.isFinite(z)) {
      return undefined;
    }

    try {
      const scanCenter = vecFromLatLong(scan.location.latitudeMean, scan.location.longitudeMean, z);
      if (
        !Number.isFinite(scanCenter.x) ||
        !Number.isFinite(scanCenter.y) ||
        !Number.isFinite(scanCenter.z)
      ) {
        return undefined;
      }
      return scanCenter;
    } catch (_e) {
      return undefined;
    }
  }

  return undefined;
};

const selectScanProcessingResult = (state: RootState) => {
  const scan = selectedScan(state);
  if (!scan?.selectedProcessingResultId) return undefined;
  return processingResultSelectors.selectById(scan.selectedProcessingResultId)(state);
};

const selectScanPoint = createSelector(
  [selectedScan, selectScanProcessingResult],
  (scan, processingResult): Vector | undefined => getScanPoint(scan, processingResult),
);

const selectScanPointById =
  (scanId: string) =>
  (state: RootState): Vector | undefined => {
    const scan = scanSelectors.selectById(scanId)(state);
    if (!scan) return undefined;
    const processingResult = scan.selectedProcessingResultId
      ? processingResultSelectors.selectById(scan.selectedProcessingResultId)(state)
      : undefined;
    return getScanPoint(scan, processingResult);
  };

const selectElementPoint = createSelector(
  [
    (state: RootState) => {
      const element = firstSelectedElement(state);
      return (element as PointElement)?.pointId ?? (element as SplineElement)?.fromPoint?.id;
    },
    (state: RootState) => state,
  ],
  (elementPointId, state) => {
    if (!elementPointId) return undefined;
    return pointSelectors.selectById(elementPointId)(state);
  },
);

const selectCameraGoTo = (state: RootState) => state.viewer3d.camera.goTo;

const selectCameraGotoVector = createSelector([selectCameraGoTo], (goTo): Vector3 | undefined => {
  if (!goTo) return undefined;
  const x = goTo.x ?? 0;
  const y = goTo.y ?? 0;
  const z = goTo.z ?? 0;
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
    return undefined;
  }
  return new Vector3(x, y, z);
});

const selectedPoint = createSelector(
  [selectedPointId, (state: RootState) => state],
  (pointId, state) => {
    if (!pointId) return undefined;
    return pointSelectors.selectById(pointId)(state);
  },
);

const selectAllScansRaw = scanSelectors.selectAll;

const allScans = createSelector([selectProjectId, selectAllScansRaw], (projectId, scans) =>
  scans.filter((scan) => scan.projectId === projectId),
);

export const viewer3dSelectors = {
  selectScanPoint,
  selectScanPointById,
  selectElementPoint,
  cameraViewport: selectCameraViewport,
  selectedPointId,
  selectedPoint,
  cameraGoto: selectCameraGotoVector,
  initialCameraSet: getViewer3dProp<boolean>('initialCameraSet'),
  mapStyle: getViewer3dProp<MapboxStyle>('mapStyle'),
  visibleScans: getViewer3dProp<VisibleScans>('visibleScans'),
  selectedProjectId: selectProjectId,
  selectedProject,
  selectedScanId,
  selectedScan,
  selectedTrenchId,
  selectedElementIds,
  scanSelection: getViewer3dProp<ScanSelection | undefined>('scanSelection'),
  visibleElementUsage: selectVisibleElementUsage,
  allScans,
  scanIdIsSelected:
    (scanId?: string) =>
    (state: RootState): boolean =>
      scanId === state.viewer3d.selectedScanId,
  isTrenchIdSelected:
    (trenchId?: string) =>
    (state: RootState): boolean =>
      !!trenchId && state.viewer3d.selectedTrenchId === trenchId,
};
